import { useLazyQuery, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';

import { ROUTE_RETURNS_MANIFEST } from '@/constants/featureFlags';
import { GET_JOB_INFO } from '@/components/admin/AdminExceptions/graphql';
import { INSERT_MANIFEST_AND_UPSERT_ITEMS } from '@/components/Manifests/graphql';
import { GET_ORDER_ROUTE_CARRIER } from '@/graphql/queries/order_route_mappings';
import { QUERY_BY_PK_WITH_USER } from '@/graphql/queries/clients';

// Shared return-creation flow for every page that resolves an exception into a return: link the new
// return to its parent, then create a standalone INBOUND manifest unless the onward-functions
// route-returns trigger owns it (parent routed + route owner's flag on).

const DELIVERY_LEG_TYPES = ['FULL', 'DROPOFF'];

// unresolved → defer to trigger. Flag on: goods already on a returns manifest (a recorded attempt, or a
// route submit) are owned by the trigger; otherwise non-routed returns get a standalone, and routed ones
// only for warehouse returns (a customer return joins its pickup route's manifest at submit).
// Flag off → legacy wh-return standalone.
export const shouldCreateStandaloneManifest = ({ isWhReturn, routed, flagOn, resolved, onReturnsManifest }) => {
    if (!resolved) {
        return false;
    }
    if (flagOn) {
        if (onReturnsManifest) {
            return false;
        }
        return !routed || !!isWhReturn;
    }
    return !!isWhReturn;
};

const JOB_POLL_ATTEMPTS = 8;
const JOB_POLL_INTERVAL_MS = 500;

export const useReturnOrderFlow = ({ setReturnOrderId }) => {
    const [getJobInfo] = useLazyQuery(GET_JOB_INFO, {
        fetchPolicy: 'network-only',
        onError: (err) => captureException(err),
    });
    const [getOrderRouteCarrier] = useLazyQuery(GET_ORDER_ROUTE_CARRIER, { fetchPolicy: 'network-only' });
    const [getCarrierClient] = useLazyQuery(QUERY_BY_PK_WITH_USER, { fetchPolicy: 'cache-first' });
    const [insertManifestAndUpsertItems] = useMutation(INSERT_MANIFEST_AND_UPSERT_ITEMS, {
        onError: (err) => captureException(err),
    });

    // The return order is created async; poll until it and its items exist.
    const pollForReturnOrder = async (job_id) => {
        let newOrder = null;
        for (let i = 0; i < JOB_POLL_ATTEMPTS; i++) {
            const res = await getJobInfo({ variables: { job_id } });
            newOrder = res?.data?.jobs_by_pk?.orders?.[0] || newOrder;
            if (newOrder?.itemsByOrderId?.length) break;
            await new Promise((resolve) => {
                setTimeout(resolve, JOB_POLL_INTERVAL_MS);
            });
        }
        return newOrder;
    };

    // { routed, flagOn, resolved, onReturnsManifest } for a return's parent. resolved:false = ownership
    // unknown (query failed / routed owner's flag unreadable) → don't create a standalone, or it
    // double-creates. useLazyQuery reports failures as `error` rather than throwing, so check it explicitly.
    const resolveReturnOwnership = async (parentOrderId) => {
        if (!parentOrderId) {
            return { routed: false, flagOn: false, resolved: false };
        }
        const { data, error } = await getOrderRouteCarrier({ variables: { order_id: parentOrderId } });
        if (error || !data) {
            return { routed: false, flagOn: false, resolved: false };
        }
        const mappings = data?.order_route_mappings || [];
        const mapping =
            mappings.find((m) => DELIVERY_LEG_TYPES.includes(m.type) && m.route) || mappings.find((m) => m.route);
        const route = mapping?.route || null;
        const routed = !!route?.route_id;
        const onReturnsManifest = (data?.returns_manifest_items || []).length > 0;

        // Same owner the functions trigger reads: the route's carrier (its shipper on internal routes), or
        // the order's carrier when it isn't routed.
        const ownerId = routed ? route.carrier_id || route.shipper_id : data?.order?.carrier_id;
        if (!ownerId) {
            return { routed, flagOn: false, resolved: true, onReturnsManifest };
        }

        // Routed: the flag must be read authoritatively; an unreadable owner row is unknown, not off (else
        // double-create). Not routed: web owns either way, so an unreadable row falls back to flag-off.
        const { data: cData, error: cError } = await getCarrierClient({ variables: { client_id: ownerId } });
        const owner = cData?.clients_by_pk;
        if (cError || !owner) {
            return { routed, flagOn: false, resolved: !routed, onReturnsManifest };
        }
        return { routed, flagOn: !!owner.user?.circles?.[ROUTE_RETURNS_MANIFEST], resolved: true, onReturnsManifest };
    };

    return async (order_id, job_id, isWhReturn) => {
        const newOrder = await pollForReturnOrder(job_id);
        if (!newOrder?.order_id) return;

        setReturnOrderId(order_id, newOrder.order_id);

        const { routed, flagOn, resolved, onReturnsManifest } = await resolveReturnOwnership(order_id);
        if (!shouldCreateStandaloneManifest({ isWhReturn, routed, flagOn, resolved, onReturnsManifest })) return;
        // Don't build an empty manifest if items never resolved (also avoids the .map throw).
        if (!newOrder.itemsByOrderId?.length) return;

        insertManifestAndUpsertItems({
            variables: {
                manifest: {
                    client_id: newOrder.carrier_id,
                    type: 'INBOUND',
                    source: 'EXCEPTION',
                    items: {
                        data: newOrder.itemsByOrderId.map((item) => ({ item_id: item.item_id })),
                    },
                    share_manifest: true,
                },
                items: [],
            },
        });
    };
};
